import { createContext, FC, ReactNode, Suspense, useContext } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Routes, Route, Outlet, useLocation } from 'react-router-dom';
import PageWithTitleLayout from '../page-with-title/page-with-title-layout';
import { SettingsNavigation } from './settings-navigation';
import { Styled } from './settings-workspace.styled';
import { SettingsTab } from './props';

// Section-level actions normally come from the active tab config, while tabs
// with local UI state may pass an action directly to SettingsContentHeader.
const SettingsActionContext = createContext<ReactNode>(null);

const TabLoadingFallback = (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
        }}
    >
        <CircularProgress />
    </Box>
);

type SettingsWorkspaceProps = {
    title?: string;
    tabs: SettingsTab[];
    slot?: ReactNode;
    dynamicTitle?: boolean;
    fullWidth?: boolean;
};

const SettingsWorkspace: FC<SettingsWorkspaceProps> = ({
    title,
    tabs,
    slot,
    dynamicTitle = false,
    fullWidth = false,
}): JSX.Element => {
    const location = useLocation();
    const stepKey: string | undefined = location.pathname.split('/').at(-1);
    const activeTab = tabs.find((tab) => tab.url === stepKey);
    const hasMultipleTabs = tabs.length > 1;

    const pageTitle = dynamicTitle ? (activeTab?.label ?? title) : title;

    return (
        <PageWithTitleLayout title={pageTitle} slot={slot}>
            <Styled.Workspace
                className={[
                    !hasMultipleTabs ? 'single-tab-workspace' : '',
                    fullWidth ? 'full-width-workspace' : '',
                ]
                    .filter(Boolean)
                    .join(' ') || undefined}
            >
                {hasMultipleTabs && <SettingsNavigation tabs={tabs} />}

                <Styled.Content>
                    <SettingsActionContext.Provider value={activeTab?.action}>
                        <Suspense fallback={TabLoadingFallback}>
                            <Routes>
                                {tabs.map((tab) => (
                                    <Route
                                        key={tab.url}
                                        path={tab.url}
                                        element={tab.component}
                                    />
                                ))}
                                <Route path="*" element={<Outlet />} />
                            </Routes>
                        </Suspense>
                    </SettingsActionContext.Provider>
                </Styled.Content>
            </Styled.Workspace>
        </PageWithTitleLayout>
    );
};

export const SettingsContentHeader = ({
    title,
    description,
    action: actionProp,
}: {
    title: string;
    description?: string;
    action?: ReactNode;
}) => {
    const contextAction = useContext(SettingsActionContext);
    const action = actionProp ?? contextAction;

    return (
        <Box
            sx={{
                mb: 3,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
            }}
        >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <Typography
                    variant="h6"
                    className="font-yekan-heavy"
                    sx={{ color: 'text.primary' }}
                >
                    {title}
                </Typography>

                {description && (
                    <Typography
                        variant="body2"
                        sx={{ mt: 0.5, color: 'text.secondary' }}
                    >
                        {description}
                    </Typography>
                )}
            </Box>

            {action && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        gap: 1,
                        flex: { xs: '1 1 100%', sm: '0 0 auto' },
                        minWidth: 0,
                        alignSelf: 'flex-start',
                        '& > *': {
                            minWidth: 0,
                        },
                    }}
                >
                    {action}
                </Box>
            )}
        </Box>
    );
};

export const SettingsFormActions = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 3,
            pt: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            '& > *': {
                width: { xs: '100%', sm: 'auto' },
            },
        }}
    >
        {children}
    </Box>
);

export default SettingsWorkspace;
